import {useContext, useEffect, useState, useRef} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Button.jsx";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {useErrorTranslation} from "../../../../api/utilities/translateErrorMessage.js";

function SklikIntegrationFormInput({type, label, value, note, onChange, isMasked, onFocus, onBlur}) {

    let [id] = useState((Math.random() + 1).toString(36).substring(7));

    return <div>
        <div className={'flex gap-4'}>
            <label className='w-full my-auto text-left' htmlFor={'sklik-' + id}>
                {label}:
            </label>
        </div>
        <div>
            <input
                type={type}
                id={'sklik-' + id}
                className='w-full form-input'
                value={isMasked && value ? '●'.repeat(8) : value}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
        <div className={'flex gap-4'}>
            <div className={'w-4/12'}></div>
            <div className={'w-8/12'}>{note}</div>
        </div>
    </div>;
}

export default function SklikIntegrationForm({project, projectIntegration}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let navigate = useNavigate();
    let [sklikSettings, setSklikSettings] = useState({
        'apiKey': projectIntegration?.apiKey ?? '',
    });
    let {getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);
    let [buttonClicked, setButtonClicked] = useState(false);
    let [state, setDownloading, setResult, setError] = useDownloadState();
    let [isMasked, setIsMasked] = useState(true);

    const textareaRef = useRef(null);

    useEffect(() => {
        if (!buttonClicked) {
            return;
        }

        // Reset error message
        setError(null);

        setButtonClicked(false);
        setDownloading(true);

        new ProjectIntegrationsApi(getAuthorityConfiguration())
            .sklikIntegration({
                projectId: project.id,
                integrationId: projectIntegration?.id ?? null,
                sklikIntegrationInput: sklikSettings,
            })
            .then((result) => {

                navigate('/project/' + project.id + '/settings/integrations?integrationId=' + result.id);
                resetCurrentUserProjects();

                if (projectIntegration === null) {
                    toast.success(t('created'));
                } else {
                    if (result.errors.length > 0) {
                        toast.success(t('saved-with-errors'));
                    } else {
                        toast.success(t('saved'));
                    }
                }

            })
            .catch(e => {
                et(e).then(toast.error);
            })
            .finally(() => setDownloading(false));

    }, [buttonClicked]);

    const isButtonDisabled = isMasked || !sklikSettings.apiKey.trim();

    return (<LoadingComponent loading={state.downloading}>
        <div className={'flex flex-col gap-2'}>

            <SklikIntegrationFormInput
                type={'text'}
                label={t('api-key')}
                value={sklikSettings.apiKey}
                isMasked={isMasked}
                onFocus={() => {
                    if (isMasked) {  // Vyprázdnit pole pouze, pokud jsou zobrazeny hvězdičky
                        setIsMasked(false);
                        setSklikSettings((sklikSettings) => ({
                            ...sklikSettings,
                            apiKey: '',
                        }));
                    }
                }}
                onBlur={() => {
                    if (!sklikSettings.apiKey) {
                        setSklikSettings((sklikSettings) => ({
                            ...sklikSettings,
                            apiKey: projectIntegration?.apiKey ?? '',
                        }));
                        setIsMasked(true);
                    }
                }}
                onChange={(value) => setSklikSettings((sklikSettings) => {
                    return {
                        ...sklikSettings, apiKey: value,
                    };
                })}
            />
            {projectIntegration &&
                <div className={'mb-5 text-xs text-slate-500'}>{t('fill-if-want-change')}</div>
            }

            <div className={'min-h-4 max-h-4'}>{/* spacer */}</div>

            <div className={'flex justify-end'}>
                <Button
                    className={'button-primary'}
                    onClick={() => setButtonClicked(true)}
                    title={t('project.integration-create')}
                    disabled={isButtonDisabled}
                >
                    {projectIntegration
                        ? t('project.integration-update')
                        : t('project.integration-create')}
                </Button>
            </div>

        </div>
    </LoadingComponent>);
}
